import type { ReactNode } from "react";
import { createContext, useEffect, useState } from "react";

/**
 * Theme types available in the application
 */
export type Theme = "light" | "dark" | "system";

/**
 * Interface for the theme context
 * @interface ThemeContextType - Interfaz para el contexto de tema
 * @property {Theme} theme - El tema actual
 * @property {string} resolvedTheme - El tema resuelto (light o dark)
 * @property {Function} setTheme - Función para establecer el tema
 * @property {Function} toggleTheme - Función para alternar entre light y dark
 */
export interface ThemeContextType {
  /** Current active theme */
  theme: Theme;
  /** Current resolved theme (light or dark) */
  resolvedTheme: "light" | "dark";
  /** Function to set the theme */
  setTheme: (theme: Theme) => void;
  /** Function to toggle between light and dark */
  toggleTheme: () => void;
}

/**
 * Default theme context value
 * @constant {ThemeContextType} defaultThemeContext - Valor por defecto del contexto de tema
 */
const defaultThemeContext: ThemeContextType = {
  theme: "system",
  resolvedTheme: "light",
  setTheme: () => {},
  toggleTheme: () => {},
};

/**
 * Theme context for managing application themes
 * @constant {Context<ThemeContextType>} ThemeContext - Contexto de tema
 */
const ThemeContext = createContext<ThemeContextType | undefined>(defaultThemeContext);

/**
 * Props for the ThemeProvider component
 * @interface ThemeProviderProps - Interfaz para las props del ThemeProvider
 * @property {ReactNode} children - Componentes hijos a envolver
 * @property {Theme} defaultTheme - Tema por defecto
 * @property {string} storageKey - Clave de almacenamiento para el tema
 */
interface ThemeProviderProps {
  /** Child components to be wrapped by the theme provider */
  children: ReactNode;
  /** Default theme to use */
  defaultTheme?: Theme;
  /** Storage key for persisting theme preference */
  storageKey?: string;
}

/**
 * Provider component that manages theme state and provides theme context
 * @param props - ThemeProviderProps
 * @returns JSX.Element
 */
export function ThemeProvider({
  children,
  defaultTheme = "system",
  storageKey = "viewmed-theme",
}: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(() => {
    // Try to get theme from localStorage
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem(storageKey) as Theme | null;
      if (stored && ["light", "dark", "system"].includes(stored)) {
        return stored;
      }
    }
    return defaultTheme;
  });

  const [resolvedTheme, setResolvedTheme] = useState<"light" | "dark">("light");

  /**
   * Resolves the actual theme based on system preference
   * @param {Theme} theme - El tema a resolver
   * @returns {string} El tema resuelto (light o dark)
   */
  const resolveTheme = (theme: Theme): "light" | "dark" => {
    if (theme === "system") {
      return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
    }
    return theme;
  };

  /**
   * Sets the theme and persists it to localStorage
   * @param {Theme} newTheme - El tema a establecer
   */
  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    localStorage.setItem(storageKey, newTheme);
  };

  /**
   * Toggles between light and dark themes
   * @returns {void}
   */
  const toggleTheme = (): void => {
    const newTheme = theme === "light" ? "dark" : "light";
    setTheme(newTheme);
  };

  /**
   * Applies the theme to the document
   * @param {string} theme - El tema a aplicar
   * @returns {void}
   */
  const applyTheme = (theme: "light" | "dark"): void => {
    const root = document.documentElement;

    // Remove existing theme classes
    root.classList.remove("light", "dark");

    // Add new theme class
    root.classList.add(theme);

    // Set data attribute for CSS custom properties
    root.setAttribute("data-theme", theme);
  };

  // Effect to handle theme changes
  useEffect(() => {
    const resolved = resolveTheme(theme);
    setResolvedTheme(resolved);
    applyTheme(resolved);

    // Listen for system theme changes
    if (theme === "system") {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      const handleChange = () => {
        const newResolved = resolveTheme(theme);
        setResolvedTheme(newResolved);
        applyTheme(newResolved);
      };

      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }
  }, [theme]);

  // Effect to handle initial theme application
  useEffect(() => {
    const resolved = resolveTheme(theme);
    applyTheme(resolved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Valor del contexto de tema
   * @constant {ThemeContextType} contextValue - Valor del contexto de tema
   */
  const contextValue: ThemeContextType = {
    theme,
    resolvedTheme,
    setTheme,
    toggleTheme,
  };

  return <ThemeContext.Provider value={contextValue}>{children}</ThemeContext.Provider>;
}

export { ThemeContext };
